'use client'

import { cn } from '@/lib/utils'
import { Shimmer } from './shimmer'
import { type ComponentProps, useState } from 'react'

export type AnalyzeButtonStatus = 'default' | 'analyzing' | 'done'
export type AnalyzeButtonVariant = 'default' | 'simple'

export interface AnalyzeButtonProps extends Omit<ComponentProps<'button'>, 'children'> {
    /**
     * The current status of the button
     * @default 'default'
     */
    status?: AnalyzeButtonStatus

    /**
     * Whether cached analysis exists (automatically shows "See Analysis")
     * @default false
     */
    hasAnalysis?: boolean

    /**
     * Button variant - 'simple' for a basic button without state management
     * @default 'default'
     */
    variant?: AnalyzeButtonVariant

    /**
     * Callback when the analyze action is triggered
     */
    onAnalyze?: () => void

    /**
     * Callback when viewing completed analysis
     */
    onViewAnalysis?: () => void

    /**
     * Custom text for each state (optional)
     */
    text?: {
        default?: string
        analyzing?: string
        done?: string
    }

    /**
     * Additional className for the text elements
     */
    textClassName?: string
}

export function AnalyzeButton({
    status = 'default',
    hasAnalysis = false,
    variant = 'default',
    onAnalyze,
    onViewAnalysis,
    text = {
        default: 'Analyze',
        analyzing: 'Analyzing',
        done: 'See Analysis',
    },
    className,
    textClassName,
    disabled,
    ...props
}: AnalyzeButtonProps) {
    const [isHovered, setIsHovered] = useState(false)

    // For simple variant, always use default status and ignore hasAnalysis
    const isSimple = variant === 'simple'
    const effectiveStatus = isSimple ? 'default' : hasAnalysis ? 'done' : status

    // Determine the button text based on effective status
    const getButtonText = () => {
        // For simple variant, always return default text
        if (isSimple) {
            return text.default
        }
        switch (effectiveStatus) {
            case 'analyzing':
                return text.analyzing
            case 'done':
                return text.done
            default:
                return text.default
        }
    }

    // Determine the CSS class based on effective status and hover state
    // For simple variant, use default styling with hover support
    const getButtonClass = () => {
        if (isSimple) {
            return isHovered ? 'analyze-btn-hover' : 'analyze-btn-default'
        }
        if (effectiveStatus === 'analyzing') return 'analyze-btn-active'
        if (effectiveStatus === 'done') return 'analyze-btn-done'
        if (isHovered) return 'analyze-btn-hover'
        return 'analyze-btn-default'
    }

    // Determine the text CSS class based on effective status and hover state
    // For simple variant, use default text styling with hover support
    const getTextClass = () => {
        if (isSimple) {
            return isHovered ? 'analyze-btn-hover-text' : 'analyze-btn-default-text'
        }
        if (effectiveStatus === 'analyzing') return 'analyze-btn-active-text'
        if (effectiveStatus === 'done') return 'analyze-btn-done-text'
        if (isHovered) return 'analyze-btn-hover-text'
        return 'analyze-btn-default-text'
    }

    // Handle button click
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) return

        // For simple variant, just call onClick
        if (isSimple) {
            props.onClick?.(e)
            return
        }

        if (effectiveStatus === 'done' && onViewAnalysis) {
            onViewAnalysis()
        } else if (effectiveStatus === 'default' && onAnalyze) {
            onAnalyze()
        }

        // Call parent onClick if provided
        props.onClick?.(e)
    }

    const buttonText = getButtonText()
    const textClass = getTextClass()

    return (
        <button
            type="button"
            disabled={disabled || (!isSimple && effectiveStatus === 'analyzing')}
            className={cn(
                'relative inline-flex items-center justify-center gap-2 px-4 py-2 min-h-9',
                'font-medium text-sm',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                getButtonClass(),
                className
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick}
            {...props}
        >
            {!isSimple && effectiveStatus === 'analyzing' ? (
                <Shimmer className={cn('font-medium text-sm', textClass, textClassName)}>
                    {buttonText || ''}
                </Shimmer>
            ) : (
                <span className={cn(textClass, textClassName)}>{buttonText}</span>
            )}
        </button>
    )
}
